import ConnectionsController from '@mm/state/controllers/ConnectionsController';
import { ConnectionVariableModel } from '@mm/state/model/ConnectionVariableModel';
import { TestConnectionModalProps } from '@mm/state/modules/modalsState/props';
import { getErrorJson } from '@mm/utils';
import classnames from 'classnames';
import React from 'react';
import { ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { compose, withHandlers, withStateHandlers } from 'recompose';
import Button from '../../buttons/DeprecatedButton/DeprecatedButton';
import { RouterComponentProps } from '../../helpers/routes/interfaces';
import { ModalInstanceProps } from '../interfaces';
import ErrorPane from './ErrorPane/ErrorPane';
import SendRequestPane from './SendRequestPane/SendRequestPane';
import SuccessPane from './SuccessPane/SuccessPane';
import './TestConnectionModal.scss';

export type ExtendedVariablesModel = ConnectionVariableModel & {
  [key: string]: any;
};

const enum Step {
  Initial = 'Initial',
  Success = 'Success',
  Error = 'Error',
}

export type TestConnectionParams = {
  facebookPageId: number;
  pluginId: number;
  connectionId: number;
  urlVariables: UrlVariablesLocal[];
  variables: ConnectionVariableModel[];
};

export type UrlVariablesLocal = { name: string; value: string };

type State = {
  urlVariablesLocal: UrlVariablesLocal[];
  variablesLocal: ExtendedVariablesModel[];
  requestResponse?: string;
  requestError?: { [key: string]: any };
  currentStep: Step;
  recommendedMapping: string[];
};

export type SetVariablesLocal = (
  variablesLocal: ExtendedVariablesModel[],
) => void;
export type SetUrlVariablesLocal = (
  setUrlVariablesLocal: UrlVariablesLocal[],
) => void;

type StateHandlers = {
  setUrlVariablesLocal: SetUrlVariablesLocal;
  setVariablesLocal: SetVariablesLocal;
  setRequestResponse: (requestResponse: string) => void;
  setCurrentStep: (currentStep: Step) => void;
  setRequestError: (requestError) => void;
  setRecommendedMapping: (recommendedMapping: string[]) => void;
};

type Handlers = {
  sendTestConnection: () => Promise<void>;
};

type InnerProps = RouterComponentProps &
  State &
  Handlers &
  StateHandlers &
  ModalInstanceProps;

type Props = TestConnectionModalProps & InnerProps;

const testConnection = async (params: TestConnectionParams) => {
  const { facebookPageId, connectionId, variables, urlVariables } = params;

  try {
    const response = await ConnectionsController.testWebhook(
      facebookPageId,
      connectionId,
      variables,
      urlVariables,
    );
    return response;
  } catch (e) {
    return e;
  }
};

const initialState = (ownProps: TestConnectionModalProps): State => {
  const variablesLocal = [...ownProps.variables];
  const urlVariablesLocal = ownProps.urlVariables.map(variable => {
    return { name: variable, value: '' };
  });

  return {
    urlVariablesLocal,
    variablesLocal,
    requestResponse: undefined,
    requestError: undefined,
    currentStep: Step.Initial,
    recommendedMapping: [],
  };
};

const stateUpdaters = {
  setUrlVariablesLocal: () => urlVariablesLocal => ({ urlVariablesLocal }),
  setVariablesLocal: () => variablesLocal => ({ variablesLocal }),
  setRequestResponse: () => requestResponse => ({ requestResponse }),
  setCurrentStep: () => currentStep => ({ currentStep }),
  setRequestError: () => requestError => ({ requestError }),
  setRecommendedMapping: () => (recommendedMapping: string[]) => ({
    recommendedMapping,
  }),
};

export const getWasSuccess = (response: WeirdResponseType) => {
  const keys = Object.keys(response);

  if (response.response && response.response.error) {
    return false;
  }

  return !!(keys.length === 1 && keys.indexOf('response') !== -1);
};

// Not exactly sure if this is a possible response but we are checking
// for response.response
type WeirdResponseType = Response & { response: { [key: string]: any } };

const getResponseErrors = async (
  response: WeirdResponseType,
): Promise<{ [key: string]: any } | string> => {
  if ((response as any).json) {
    return getErrorJson(response);
  }

  return response.response ? response.response : response;
};

const sendTestConnection = (props: Props) => async () => {
  const {
    connection,
    connectedPageId,
    variablesLocal,
    setRequestResponse,
    setRequestError,
    setCurrentStep,
    urlVariablesLocal,
    mappedVariables,
    setRecommendedMapping,
    shouldMapResponse,
  } = props;
  const response = await testConnection({
    facebookPageId: connectedPageId,
    pluginId: connection.pluginId,
    connectionId: connection.id,
    urlVariables: urlVariablesLocal,
    variables: variablesLocal,
  });
  const wasSuccess = getWasSuccess(response);

  if (wasSuccess) {
    const responseIsString = typeof response.response === 'string';
    const responseDataToShow = responseIsString
      ? response.response
      : JSON.stringify(response.response);
    setRequestResponse(responseDataToShow);
    setCurrentStep(Step.Success);
    if (shouldMapResponse && !responseIsString) {
      const paths = Object.keys(response.response);
      const mappedPaths = mappedVariables.map(variable => variable.receivedAs);

      const recommended = paths.filter(p => !mappedPaths.includes(p));
      setRecommendedMapping(recommended);
    }
  } else {
    const responseErrors = await getResponseErrors(response);

    setRequestError(responseErrors);
    setCurrentStep(Step.Error);
  }
};

const enhance = compose<InnerProps, TestConnectionModalProps>(
  withStateHandlers(initialState, stateUpdaters),
  withHandlers({ sendTestConnection }),
);

const TestConnectionModal = (props: Props) => {
  const {
    closeModal,
    url,
    connection,
    sendTestConnection,
    currentStep,
    requestError,
    variablesLocal,
    setVariablesLocal,
    requestResponse,
    urlVariablesLocal,
    setUrlVariablesLocal,
    recommendedMapping,
    onNewMappingFound,
    shouldMapResponse,
  } = props;

  const isInitialStep = currentStep === Step.Initial;
  const closeButtonText = isInitialStep ? 'Cancel' : 'Done';

  const modalWrapperClass = classnames('test-connection-modal__wrapper', {
    'test-connection-modal__wrapper--initial': isInitialStep,
    'test-connection-modal__wrapper--error': currentStep === Step.Error,
    'test-connection-modal__wrapper--success': currentStep === Step.Success,
  });

  return (
    <div className="test-connection-modal">
      <ModalHeader
        toggle={closeModal}
        className="test-connection-modal__header"
      >
        Test Connection
      </ModalHeader>

      <ModalBody className="test-connection-modal__body">
        <div className={modalWrapperClass}>
          <ErrorPane error={requestError} />

          <SendRequestPane
            urlVariablesLocal={urlVariablesLocal}
            variablesLocal={variablesLocal}
            setUrlVariablesLocal={setUrlVariablesLocal}
            setVariablesLocal={setVariablesLocal}
            connection={connection}
            url={url}
          />

          <SuccessPane
            responseHtml={requestResponse}
            unmappedVariablesCount={recommendedMapping.length}
          />
        </div>
      </ModalBody>

      <ModalFooter>
        {isInitialStep && (
          <Button onClick={sendTestConnection}>Send Test</Button>
        )}

        <Button
          invert
          red
          onClick={() => {
            if (
              shouldMapResponse &&
              onNewMappingFound &&
              currentStep === Step.Success &&
              recommendedMapping.length > 0
            ) {
              onNewMappingFound(recommendedMapping);
            }
            closeModal();
          }}
        >
          {closeButtonText}
        </Button>
      </ModalFooter>
    </div>
  );
};

export default enhance(TestConnectionModal);
